import { t } from '@ctcells/i18n'

import { Button } from './Button.tsx'

import './pagination.css'

/**
 * 쪽 넘김 — 「‹ 이전 · n / m 쪽 · 다음 ›」(2026-09-30 · 상시 작업 줄 이벤트 칸이 처음 쓴다).
 *
 * - `page` 는 **1부터** 센다. 범위 밖 값을 주면 끝으로 붙여 그린다(화면이 쪽 수를 앞질러도 안 깨진다)
 * - 🔴 **첫 쪽 · 끝 쪽의 비활성에는 사유를 달지 않는다**(팀장 지시 2026-09-30). 「첫 쪽이라 이전이
 *   없다」는 상태 표시(`n / m 쪽`)가 이미 말한다. 그래서 `disabledReason` 을 빈 값으로 넘긴다 —
 *   `useDisabled` 가 빈 사유면 툴팁·숨김 문구를 만들지 않는다. ⚠ 다른 버튼에서 이 흉내를 내지 마라
 * - 한 쪽뿐일 때 감출지는 **부르는 쪽**이 정한다(이 원자는 늘 그린다)
 * - 쪽을 어떻게 받는지(서버 `offset` · 받은 목록 자르기)는 모른다 — 번호만 올린다
 */
export interface PaginationProps {
  /** 1부터 */
  page: number
  /** 전체 쪽 수. 1 이상 */
  pageCount: number
  onChange: (page: number) => void
  /** 탐색 영역의 접근성 이름. 기본 「쪽 넘김」 */
  label?: string | undefined
}

export function Pagination({ page, pageCount, onChange, label }: PaginationProps) {
  const pages = Math.max(1, Math.floor(pageCount))
  const current = Math.min(Math.max(1, Math.floor(page)), pages)
  const atFirst = current <= 1
  const atLast = current >= pages
  return (
    <nav aria-label={label ?? t('pagination.label')} className="a-pager">
      <Button
        size="sm"
        onClick={() => onChange(current - 1)}
        {...(atFirst ? { disabled: true, disabledReason: '' } : {})}
      >
        {/* `.a-btn` 은 격자라 자식마다 한 줄이 된다 — 글리프와 글을 한 덩이로 묶는다 */}
        <span>
          <span aria-hidden="true">‹</span> {t('pagination.prev')}
        </span>
      </Button>
      <span aria-live="polite" className="a-pager__status num">
        {t('pagination.status', { page: current, pages })}
      </span>
      <Button
        size="sm"
        onClick={() => onChange(current + 1)}
        {...(atLast ? { disabled: true, disabledReason: '' } : {})}
      >
        <span>
          {t('pagination.next')} <span aria-hidden="true">›</span>
        </span>
      </Button>
    </nav>
  )
}
